import { useQuery } from "@tanstack/react-query";

import { db } from "../lib/db-client";
import { useAuthContext } from "../contexts/AuthContext";
import { useProfileContext, Profile, UpdateProfileInput, UserStats } from "../contexts/ProfileContext";
import { coreQueryKeys } from "../lib/query-keys";

// Re-export types for backwards compatibility
export type { Profile, UpdateProfileInput, UserStats };

/**
 * useProfile hook - provides profile state and methods
 * This is a wrapper around ProfileContext for backwards compatibility
 */
export const useProfile = () => {
  const profileContext = useProfileContext();

  return {
    profile: profileContext.profile,
    isLoading: profileContext.isLoading,
    error: profileContext.error,
    updateProfile: profileContext.updateProfile,
    isUpdating: profileContext.isUpdating,
    // New API
    refreshProfile: profileContext.refreshProfile,
  };
};

export interface UseUserStatsOptions {
  /**
   * Fetch only when the surrounding surface is actually on screen.
   *
   * This hook used to live on `ProfileProvider`, which sits at the app root, so
   * every page load paid for it. Callers that render inside a closed dialog must
   * pass their `open` flag through here.
   */
  enabled?: boolean;
}

/**
 * Aggregate account stats — practice counts, group count, time tracked.
 *
 * Opt-in on purpose: four aggregate queries are not worth paying for on a page
 * that renders none of them. Only the profile dialog asks for this today.
 *
 * Three things changed from the version that lived on the context:
 *  - the "last activity" value rides on the same query as `totalSessions`
 *    (`{ count: "exact" }` with `limit(1)`), so one request answers both;
 *  - `accountCreated` reads the already-cached profile instead of issuing a
 *    second `profiles` request (and no longer falls back to `auth.getUser()`,
 *    which is another round trip for a field that is already in hand);
 *  - everything runs in one `Promise.all`, so the dialog no longer waits for a
 *    sequential second hop.
 */
export const useUserStats = ({ enabled = true }: UseUserStatsOptions = {}) => {
  const { userId } = useAuthContext();
  const { profile } = useProfileContext();
  const accountCreated = profile?.created_at ?? null;

  const query = useQuery({
    queryKey: coreQueryKeys.userStats(userId),
    enabled: enabled && !!userId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 3;
    },
    queryFn: async (): Promise<UserStats | null> => {
      if (!userId) return null;

      const [sessionsResult, publicSessionsResult, groupsResult, timeResult] = await Promise.all([
        // Count + newest row in one request: `count` is the header, `limit(1)`
        // is the last-activity date.
        db.from("practice_sessions")
          .select("created_at", { count: "exact" })
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(1),
        db.from("practice_sessions")
          .select("id", { count: "exact" })
          .eq("user_id", userId)
          .eq("is_public", true)
          .limit(1),
        db.from("study_group_members")
          .select("id", { count: "exact" })
          .eq("user_id", userId)
          .limit(1),
        db.from("topic_time" as "practice_sessions")
          .select("total_seconds")
          .eq("user_id", userId),
      ]);

      if (sessionsResult.error) throw sessionsResult.error;
      if (publicSessionsResult.error) throw publicSessionsResult.error;
      if (groupsResult.error) throw groupsResult.error;

      const timeRows = (timeResult.data ?? []) as unknown as Array<{ total_seconds?: number }>;
      const totalTimeSeconds = timeRows.reduce((sum, row) => sum + (row.total_seconds ?? 0), 0);

      return {
        totalSessions: sessionsResult.count ?? 0,
        totalGroups: groupsResult.count ?? 0,
        publicSessions: publicSessionsResult.count ?? 0,
        accountCreated,
        lastActivity: sessionsResult.data?.[0]?.created_at ?? null,
        totalTimeSeconds,
        topicCount: timeRows.length,
      };
    },
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
  };
};
